import { SCROLL_INTO_VIEW_TO_NEAREST } from '../../../constants/common'
import { DAY } from '../../../constants/date'
import { QA_ELEMENT_KEY } from '../../../constants/qa'
import { getQaSelector } from '../../../utils/common'

import {
  GRETA_AI_CHAT_CHAT_LIST_SECTIONS,
  GRETA_AI_CHAT_CHATS_SECTION,
  GRETA_AI_CHAT_PAST_30_DAYS,
  GRETA_AI_CHAT_PAST_WEEK_DAYS,
  GretaAIChatChatSectionKey,
  QA_NAME_GRETA_AI_CHAT_CHAT,
} from './GretaAIChatChatList.constants'

function getStartOfTodayMs(now) {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
}

function byGretaAIChatChatRecency(firstChat, secondChat) {
  return secondChat.updatedAt - firstChat.updatedAt
}

// Chats with no timestamp land in "Past 30 days" (and, being 0, sort to its end).
function getGretaAIChatChatSectionKey(
  updatedAt,
  { past30Days, pastWeek, today }
) {
  if (updatedAt >= today) {
    return GretaAIChatChatSectionKey.today
  }

  if (updatedAt >= pastWeek) {
    return GretaAIChatChatSectionKey.pastWeek
  }

  if (updatedAt >= past30Days || !updatedAt) {
    return GretaAIChatChatSectionKey.past30Days
  }

  return GretaAIChatChatSectionKey.older
}

function getGretaAIChatChatsSections(chats) {
  return [
    {
      ...GRETA_AI_CHAT_CHATS_SECTION,
      chats: chats.toSorted(byGretaAIChatChatRecency),
    },
  ]
}

export function groupGretaAIChatChats(chats, now) {
  const today = getStartOfTodayMs(now)
  const thresholds = {
    past30Days: today - GRETA_AI_CHAT_PAST_30_DAYS * DAY,
    pastWeek: today - GRETA_AI_CHAT_PAST_WEEK_DAYS * DAY,
    today,
  }

  const chatsBySectionKey = {
    [GretaAIChatChatSectionKey.older]: [],
    [GretaAIChatChatSectionKey.past30Days]: [],
    [GretaAIChatChatSectionKey.pastWeek]: [],
    [GretaAIChatChatSectionKey.today]: [],
  }

  for (const chat of chats) {
    chatsBySectionKey[
      getGretaAIChatChatSectionKey(chat.updatedAt, thresholds)
    ].push(chat)
  }

  return GRETA_AI_CHAT_CHAT_LIST_SECTIONS.map(section => ({
    chats: chatsBySectionKey[section.key].sort(byGretaAIChatChatRecency),
    key: section.key,
    titleKey: section.titleKey,
  })).filter(section => section.chats.length > 0)
}

export function getGretaAIChatChatListSections({
  chats,
  isProjectsEnabled,
  now,
}) {
  return isProjectsEnabled
    ? getGretaAIChatChatsSections(chats)
    : groupGretaAIChatChats(chats, now)
}

export function getGretaAIChatChatsOutsideProjects(chats, projectChatIds) {
  return chats.filter(chat => !projectChatIds.has(chat.id))
}

export function getIsGretaAIChatSelectionChanged(activeChatId, seenChatId) {
  return Boolean(activeChatId) && activeChatId !== seenChatId
}

export function revealGretaAIChatChat(node, chatId) {
  const { [QA_ELEMENT_KEY]: chatSelectorValue } = getQaSelector({
    context: chatId,
    value: QA_NAME_GRETA_AI_CHAT_CHAT,
  })

  node
    .querySelector(`[${QA_ELEMENT_KEY}="${chatSelectorValue}"]`)
    ?.scrollIntoView(SCROLL_INTO_VIEW_TO_NEAREST)
}

export function getGretaAIChatChatListFlags({ hasChats, hasError, isLoading }) {
  return {
    isEmpty: !hasChats && !isLoading && !hasError,
    isError: !hasChats && !isLoading && hasError,
    isLoading: !hasChats && isLoading,
    isRows: hasChats,
  }
}
